/**
 * Directly ported from our Vuex implementation at @/store/commenting.js, comments included.
 */
import { defineStore } from 'pinia';
import { ref, reactive, computed } from 'vue';
import dayjs from 'dayjs';
import * as CollaborationAPI from '@/apis/collaboration';
import humps from 'humps';

export const useCommentingStore = defineStore('commenting', () => {
  const users = ref({});
  const notifications = ref([]);
  const notificationsPaging = ref({});
  const unreadNotifications = ref();
  const unreadNotificationsPaging = ref();
  const comments = ref([]);
  const newComment = ref(null);
  const newCommentText = ref('');
  const pending = reactive({
    comments: false,
    newComment: false,
    notificationsPaging: false,
    notifications: false,
    unreadNotifications: false,
  });

  function addUsers(page) {
    if (page?.referencedObjects?.user) {
      users.value = {
        ...users.value,
        ...page.referencedObjects.user,
      };
    }
  }

  const hasUnsavedComments = computed(
    () =>
      newCommentText.value?.trim() ||
      comments.value.some(
        (comment) =>
          comment._reply_text?.trim() ||
          (comment._edit_text && comment._edit_text !== comment.text) ||
          comment.replies?.some?.((reply) => reply._edit_text && reply._edit_text !== reply.text),
      ),
  );

  async function getNotifications() {
    pending.notifications = true;
    try {
      const response = await CollaborationAPI.getNotifications();
      const page = response?.data;
      notifications.value = page.data || [];
      notificationsPaging.value = page.paging;
      addUsers(page);
    } finally {
      pending.notifications = false;
    }
  }

  async function getNotificationsNextPage() {
    const next = notificationsPaging.value?.next;
    const loading = pending.notificationsPaging;
    if (next && !loading) {
      pending.notificationsPaging = true;
      try {
        const response = await CollaborationAPI.getNotifications({ next });
        const page = response?.data;
        const existingNotificationIds = notifications.value.map((notification) => notification.id);
        const newNotifications = (page?.data || []).filter(
          (notification) => !existingNotificationIds.includes(notification.id),
        );
        notifications.value = [...notifications.value, ...newNotifications];
        notificationsPaging.value = page.paging;
        addUsers(page);
      } finally {
        pending.notificationsPaging = false;
      }
    }
  }

  async function getUnreadNotifications() {
    if (!pending.unreadNotifications) {
      pending.unreadNotifications = true;
      try {
        const next = unreadNotificationsPaging.value?.next;
        const response = await CollaborationAPI.getNotifications({ next, isRead: false });
        const page = response?.data;
        const existingNotificationIds = unreadNotifications.value?.map(
          (notification) => notification.id,
        );
        const newUnreadNotifications = (page?.data || []).filter(
          (notification) => !existingNotificationIds?.includes?.(notification.id),
        );
        unreadNotifications.value = [
          ...(unreadNotifications.value || []),
          ...newUnreadNotifications,
        ];
        unreadNotificationsPaging.value = page.paging;
        addUsers(page);
      } finally {
        pending.unreadNotifications = false;
      }
    }
  }

  function resetUnreadNotifications() {
    unreadNotifications.value = undefined;
    unreadNotificationsPaging.value = undefined;
  }

  function optimisticallyUpdateNotifications(shouldUpdateFn) {
    notifications.value = notifications.value.map((notification) => ({
      ...notification,
      readAt: shouldUpdateFn(notification)
        ? notification.readAt || dayjs().toISOString()
        : notification.readAt,
      originalReadAt: notification.readAt,
    }));

    if (unreadNotifications.value) {
      unreadNotifications.value = unreadNotifications.value.map((notification) => ({
        ...notification,
        readAt: shouldUpdateFn(notification)
          ? notification.readAt || dayjs().toISOString()
          : notification.readAt,
        originalReadAt: notification.readAt,
      }));
    }
  }

  function rollbackNotifications(shouldUpdateFn) {
    notifications.value = notifications.value.map(({ originalReadAt, ...notification }) => ({
      ...notification,
      readAt: shouldUpdateFn(notification) ? originalReadAt : notification.readAt,
    }));

    if (unreadNotifications.value) {
      unreadNotifications.value = unreadNotifications.value.map(
        ({ originalReadAt, ...notification }) => ({
          ...notification,
          readAt: shouldUpdateFn(notification) ? originalReadAt : notification.readAt,
        }),
      );
    }
  }

  function updateNotificationsFromResponse(page, shouldUpdateFn) {
    if (Array.isArray(page.data)) {
      notifications.value = Object.freeze(page.data);
    } else {
      notifications.value = notifications.value.map((notification) =>
        shouldUpdateFn(notification) ? page.data : notification,
      );
    }

    if (unreadNotifications.value && page.data) {
      if (Array.isArray(page.data)) {
        unreadNotifications.value = unreadNotifications.value.map((notification) => {
          const updatedNotification = page.data.find((n) => n.id === notification.id);
          return updatedNotification || notification;
        });
      } else {
        unreadNotifications.value = unreadNotifications.value.map((notification) =>
          shouldUpdateFn(notification) ? page.data : notification,
        );
      }
    }

    addUsers(page);
  }

  async function markNotificationRead({ id }) {
    const shouldUpdate = (notification) => notification.id === id;

    optimisticallyUpdateNotifications(shouldUpdate);

    try {
      const response = await CollaborationAPI.updateNotification({ id, readAt: 'NOW' });
      const page = response?.data;

      updateNotificationsFromResponse(page, shouldUpdate);
    } catch (error) {
      rollbackNotifications(shouldUpdate);
      throw error;
    }
  }

  async function markNotificationsRead({ ids = [] } = {}) {
    const shouldUpdate = (notification) => !ids.length || ids.includes(notification.id);

    optimisticallyUpdateNotifications(shouldUpdate);

    try {
      const response = await CollaborationAPI.updateNotifications({ ids, readAt: 'NOW' });
      const page = response?.data;

      updateNotificationsFromResponse(page, shouldUpdate);
      notificationsPaging.value = page.paging;
    } catch {
      rollbackNotifications(shouldUpdate);
    }
  }

  async function getComments({ brandIds, resourceId, resourceType }) {
    pending.comments = true;
    try {
      const response = await CollaborationAPI.getComments({ brandIds, resourceId, resourceType });
      const page = response.data;
      comments.value = page?.data;
      addUsers(page);
    } finally {
      pending.comments = false;
    }
  }

  async function createComment({ brandId, resourceId, resourceType, text, parentId }) {
    pending.newComment = true;
    try {
      const response = await CollaborationAPI.createComment({
        brandId,
        resourceId,
        resourceType,
        text,
        parentId,
      });
      const payload = response?.data;
      const newCommentData = payload?.data;
      if (newCommentData.parentId) {
        comments.value = comments.value.map((comment) => ({
          ...comment,
          replies:
            newCommentData.parentId === comment.id
              ? [...(comment.replies ?? []), newCommentData]
              : comment.replies,
        }));
      } else {
        comments.value = [...comments.value, newCommentData];
      }
      addUsers(payload);
    } finally {
      pending.newComment = false;
    }
  }

  async function updateComment({ id, text }) {
    comments.value = comments.value.map((comment) => ({
      ...comment,
      replies: comment.replies?.map?.((reply) => ({
        ...reply,
        _saving: reply.id === id || reply._saving,
      })),
      _saving: comment.id === id || comment._saving,
    }));

    try {
      const response = await CollaborationAPI.updateComment({ id, text });
      const payload = response.data;
      const updatedComment = payload?.data;
      comments.value = comments.value.map((comment) => {
        if (updatedComment.parentId === comment.id) {
          return {
            ...comment,
            replies: comment.replies?.map?.((reply) =>
              reply.id === updatedComment.id ? updatedComment : reply,
            ),
          };
        }
        if (updatedComment.id === comment.id) {
          return updatedComment;
        }
        return comment;
      });
      addUsers(payload);
    } catch (error) {
      comments.value = comments.value.map((comment) => ({
        ...comment,
        replies: comment.replies?.map?.((reply) => ({
          ...reply,
          _saving: reply.id !== id && reply._saving,
        })),
        _saving: comment.id !== id && comment._saving,
      }));
      throw error;
    }
  }

  async function deleteComment({ id }) {
    await CollaborationAPI.deleteComment({ id });
    comments.value = comments.value
      .map((comment) => ({
        ...comment,
        replies: comment.replies && comment.replies.filter((reply) => reply.id !== id),
        deletedAt: comment.id === id ? dayjs().toISOString() : comment.deletedAt,
      }))
      .filter((comment) => comment.replies?.length || !comment.deletedAt);
  }

  function clearComments() {
    comments.value = [];
    newComment.value = null;
    newCommentText.value = '';
  }

  function clearUnsavedComments() {
    newComment.value = null;
    newCommentText.value = '';
    comments.value = comments.value.map((comment) => ({
      ...comment,
      _composing_reply: undefined,
      _composing_edit: undefined,
      _reply_text: undefined,
      _edit_text: undefined,
      replies: comment.replies?.map?.((reply) => ({
        ...reply,
        _composing_reply: undefined,
        _composing_edit: undefined,
        _reply_text: undefined,
        _edit_text: undefined,
      })),
    }));
  }

  function createCommentNotificationSocket({ notification }) {
    notifications.value = [humps.camelizeKeys(notification.data), ...notifications.value];
    if (notification?.referenced_objects?.user) {
      users.value = {
        ...notification.referenced_objects.user,
        ...users.value,
      };
    }
  }

  function startReply({ id }) {
    comments.value = comments.value.map((comment) => ({
      ...comment,
      _reply_text: comment.id === id ? '' : comment._reply_text,
      _composing_reply: comment.id === id || comment._composing_reply,
    }));
  }

  function setReplyText({ id, value }) {
    comments.value = comments.value.map((comment) => ({
      ...comment,
      _reply_text: comment.id === id ? value : comment._reply_text,
    }));
  }

  function cancelReply({ id }) {
    comments.value = comments.value.map((comment) => ({
      ...comment,
      _reply_text: comment.id === id ? '' : comment._reply_text,
      _composing_reply: comment.id !== id && comment._composing_reply,
    }));
  }

  function setCommentText({ value }) {
    newCommentText.value = value;
  }

  function cancelComment() {
    newCommentText.value = '';
  }

  function startEdit({ id }) {
    comments.value = comments.value.map((comment) => ({
      ...comment,
      _edit_text: comment.id === id ? comment.text : comment._edit_text,
      _composing_edit: comment.id === id || comment._composing_edit,
      replies: comment.replies?.map?.((reply) => ({
        ...reply,
        _edit_text: reply.id === id ? reply.text : reply._edit_text,
        _composing_edit: reply.id === id || reply._composing_edit,
      })),
    }));
  }

  function setEditText({ id, value }) {
    comments.value = comments.value.map((comment) => ({
      ...comment,
      _edit_text: comment.id === id ? value : comment._edit_text,
      replies: comment.replies?.map?.((reply) => ({
        ...reply,
        _edit_text: reply.id === id ? value : reply._edit_text,
      })),
    }));
  }

  function cancelEdit({ id }) {
    comments.value = comments.value.map((comment) => ({
      ...comment,
      _edit_text: comment.id === id ? '' : comment._edit_text,
      _composing_edit: comment.id !== id && comment._composing_edit,
      replies: comment.replies?.map?.((reply) => ({
        ...reply,
        _edit_text: reply.id === id ? '' : reply._edit_text,
        _composing_edit: reply.id !== id && reply._composing_edit,
      })),
    }));
  }

  return {
    users,
    notifications,
    notificationsPaging,
    unreadNotifications,
    unreadNotificationsPaging,
    comments,
    newComment,
    newCommentText,
    pending,
    hasUnsavedComments,
    getNotifications,
    getNotificationsNextPage,
    getUnreadNotifications,
    resetUnreadNotifications,
    markNotificationRead,
    markNotificationsRead,
    getComments,
    createComment,
    updateComment,
    deleteComment,
    clearComments,
    clearUnsavedComments,
    createCommentNotificationSocket,
    startReply,
    setReplyText,
    cancelReply,
    setCommentText,
    cancelComment,
    startEdit,
    setEditText,
    cancelEdit,
  };
});
